import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const transvaginalUltrasound: ContentBlock<"procedure"> = {
  id: makeId("proc-transvaginal-ultrasound"),
  kind: "procedure",
  tags: ["testing", "her", "clinic-visit", "during-cycle"],
  title: "Transvaginal ultrasound",
  subtitle:
    "Internal ultrasound for detailed imaging of the uterus and ovaries",
  body: {
    procedureName: "Transvaginal ultrasound",
    category: "diagnostic",
    description:
      "An internal ultrasound where a slim probe is gently inserted into the vagina to provide close-up, high-resolution images of the uterus, ovaries, and follicles. The most common imaging tool in fertility care.",
    howItWorks:
      "You lie on an exam table with your feet in stirrups. A slim, lubricated ultrasound probe (about the width of two fingers) is gently inserted into the vagina. Because the probe sits close to the pelvic organs, it produces detailed images of the uterine lining, ovarian follicles, and surrounding structures. During a stimulation cycle, this exam is used to count and measure growing follicles and assess endometrial thickness. The images appear on a screen in real time.",
    duration: "10–20 minutes",
    anesthesia: "none",
    whatToExpect: [
      "An empty bladder is preferred, so you may be asked to use the restroom beforehand",
      "Mild pressure but generally not painful",
      "The sonographer or doctor may gently angle the probe to see each ovary",
      "You'll often see and hear about your follicle count and lining thickness during the exam",
      "Results are typically discussed immediately or at your next appointment",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Follicle count, follicle sizes, and lining thickness are often discussed during the scan or soon after.",
      },
      {
        label: "What you learn",
        detail:
          "In treatment cycles, ultrasound helps decide medication changes, trigger timing, or whether another scan is needed.",
      },
      {
        label: "Formal review",
        detail:
          "If the scan is diagnostic, your doctor may review images later and discuss findings at a follow-up visit.",
      },
    ],
    prepInstructions: [
      "Empty your bladder before the exam unless instructed otherwise",
      "Wear comfortable clothing that is easy to change out of",
      "No special preparation or fasting required",
      "Let the sonographer know if you experience discomfort during the exam",
    ],
    risks: [
      "No known risks: ultrasound is radiation-free",
      "Mild temporary discomfort or spotting in rare cases",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Échographie endovaginale",
      description:
        "Échographie interne au cours de laquelle une sonde fine est introduite délicatement dans le vagin pour obtenir des images détaillées et haute résolution de l'utérus, des ovaires et des follicules. C'est l'examen d'imagerie le plus utilisé en AMP.",
      howItWorks:
        "Vous êtes allongée sur une table d'examen, les pieds dans des étriers. Une sonde d'échographie fine et lubrifiée (de la largeur d'environ deux doigts) est introduite délicatement dans le vagin. La sonde étant proche des organes pelviens, elle produit des images détaillées de la muqueuse utérine, des follicules ovariens et des structures environnantes. Pendant une stimulation, cet examen permet de compter et mesurer les follicules en développement et d'évaluer l'épaisseur de l'endomètre. Les images apparaissent à l'écran en temps réel.",
      duration: "10 à 20 minutes",
      whatToExpect: [
        "Vessie vide de préférence ; on peut vous demander d'aller aux toilettes au préalable",
        "Légère pression mais généralement non douloureuse",
        "L'échographiste ou le médecin peut orienter doucement la sonde pour visualiser chaque ovaire",
        "Vous verrez et entendrez souvent les informations sur le nombre de follicules et l'épaisseur de l'endomètre pendant l'examen",
        "Les résultats sont généralement discutés immédiatement ou lors du prochain rendez-vous",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "Le nombre et la taille des follicules ainsi que l'épaisseur de l'endomètre sont souvent discutés pendant l'échographie ou peu après.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "Pendant un traitement, l'échographie aide à décider des ajustements de dose, du déclenchement ou d'un prochain contrôle.",
        },
        {
          label: "Relecture formelle",
          detail:
            "Si l'échographie est diagnostique, votre médecin peut revoir les images plus tard et discuter les résultats en consultation.",
        },
      ],
      prepInstructions: [
        "Videz votre vessie avant l'examen, sauf indication contraire",
        "Portez des vêtements confortables et faciles à retirer",
        "Aucune préparation particulière ni jeûne nécessaire",
        "Signalez à l'échographiste tout inconfort pendant l'examen",
      ],
      risks: [
        "Aucun risque connu : l'échographie n'utilise pas de rayonnements ionisants",
        "Léger inconfort transitoire ou spotting dans de rares cas",
      ],
    },
  },
  sources: [
    {
      title:
        "AIUM Practice Parameter for the Performance of an Ultrasound Examination of the Female Pelvis",
      authors: "AIUM/ACR/ACOG/SRU",
      journal: "Journal of Ultrasound in Medicine",
      year: 2020,
      url: "https://onlinelibrary.wiley.com/doi/10.1002/jum.15205",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(transvaginalUltrasound);

for (const testId of [
  "test-monitoring-ultrasound",
  "test-baseline-ultrasound",
  "test-lining-check",
]) {
  registerRelationship({
    from: transvaginalUltrasound.id,
    to: makeId(testId),
    type: "related-to",
    label: "Procedure used for",
  });
}
